import React, { useState } from "react";

const InspectionForm = ({ inspection, readOnly, onClose }) => {

  const isMash = inspection.organization_name === "MASH";
  const isScarr = inspection.organization_name === "SC-NARR";
  const inspectionPassed = inspection.passed !== null;
  
  const { policy_fields = {} } = inspection;

  const questionKeys = Object.keys(policy_fields).filter(
    (key) => !key.endsWith('_label') && !key.endsWith('_custom_radio_labels')
  );

  const formatAnswer = (value, custom_radio_labels) => {
    if (value === true) return custom_radio_labels ? 'Logged' : 'Acceptable';
    if (value === false) return custom_radio_labels ? 'Flag for Follow-Up' : 'Unacceptable';
    if (value === null || value === undefined || value === '') return isMash ? 'Not Applicable' : '—';
    return value.toString();
  };

  const mashFields = [ 
    { type: "heading", label: 'Program Structure, Recovery Model & Services'},
    { field: "event_relapse", label: 'What happens in the event of a relapse?' },
    { field: "event_overdose", label: 'What happens in the event of an overdose? Where do you keep your Narcan?' },
    { field: "overdose_procedure", label: 'How are staff/residents oriented to the location of Narcan within the residence and our overdose procedure?' },
    { field: "house_meetings", label: 'Do you hold mandatory house meetings?' },
    { field: "topics_discussed", label: 'What day / time? What topics are discussed?' },
    { field: "assists_residents_recovery", label: 'Do you assist residents in finding recovery mentor or mutual aid sponsor if they are having difficulty finding one?' },
    { field: "host_social_events", label: 'Do you host and/or engage in sober social events? What events or activities have you participated in lately?' },
    { field: "resident_feedback", label: 'Do you allow residents to give feedback on their experience in order to enhance your program? How is this conducted?' },
    { field: "licensing_requirements", label: 'Does the home provide services appropriate to its NARR level of support and any state licensing requirements?' },
    { field: "home_management_seminar_completed", label: 'Have the home manager and any staff directly serving residents completed a home management seminar? If not, have they been notified of the next seminar?' },
    { field: "repairs_maintenance", label: 'Does the program have a staffing plan that provides for addressing repairs and maintenance issues?' },
    { type: "heading", label: 'Physical Environment & Safety'},
    { field: "sleeping_room", label: 'Is there a minimum of 50 sq ft per client per sleeping room?' },
    { field: "sleeping_rooms_adequate_space_and_no_locks", label: 'Do all sleeping rooms provide residents with adequate space that is free of door locks?'},
    { field: "personal_item_storage_access", label: 'Does each resident have access to personal item storage?' },
    { field: "resident_clean_storage_space", label: 'Does each resident have a responsible and clean identified storage space?' },
    { field: "furnishings_typical", label: 'Are furnishings typical to those found in a single family home or apartment as opposed to institutional settings?' },
    { field: "entrances_and_exits", label: 'Are entrances and exits home-like as opposed to institutional or clinical?' },
    { field: "fire_hazardous", label: 'Is the interior of the home functional, safe, of clean condition, and free of fire hazardous?' },
    { field: "no_hazardous_piles_near_furnace", label: 'Is the home free of piles of newspapers, clothes, and other stored materials (especially near furnaces) that would create a hazard?' },
    { field: "safe_outlets_and_extension_cords", label: 'Are there no outlets or extension cords that may be a fire hazard?' },
    { field: "exterior_fire_hazardous", label: 'Is the exterior of the home functional, safe, of clean condition, and free of fire hazards?' },
    { field: "home_free_of_paint_damage", label: 'Is the home free of large areas of stained, chipped, or peeling paint?' },
    { field: "clean_safe_floor_and_furniture", label: 'Are all floor coverings and furniture clean, of reasonable quality, and in safe condition?'},
    { field: "no_mold_mildew", label: 'Is the home free of signs of mold and mildew?' },
    { field: "no_pest_infestation", label: 'Is the home free of any signs of pest infestation problems?' },
    { field: "resident_bathrooms", label: 'Is there 1 full bathroom for every 6 residents?' },
    { field: "resident_refrigerator", label: 'Is there at least 1 refrigerator for every 7 residents?' },
    { field: "proper_refrigerator_freezer_temps", label: 'Are refrigerator temps 36°F-44°F and freezer temps at approximately 0°F?' },
    { field: "laundry_facilities_accessible", label: 'Does the property have a washer/dryer available to all clients or are laundry services provided to residents? Note: If the home does not have a washer and/or dryer for residents to use, it must be within reasonable walking distance of a laundromat to fulfill this requirement.' },
    { field: "working_order", label: 'Are the appliances clean and in working order?' },
    { field: "smoke_detector", label: 'Does each sleeping room have a functioning smoke detector?' },
    { field: "smoke_detector_each_floor", label: 'Is there a functioning smoke detector on each floor including the basement?' },
    { field: "smoke_detectors_interconnected_each_level", label: 'Is there at least one smoke detector on each level of the home with all smoke detectors being interconnected?' },
    { field: "emergency_escape", label: 'Does each sleeping room have an egress or secondary means of escape in event of emergency?' },
    { field: "gas_appliances", label: 'Are there functioning carbon monoxide detectors on each floor, if there are gas appliances?' },
    { field: "co_detector_within_sleeping_rooms", label: 'Is there a functioning carbon monoxide detector located within 9 feet of all sleeping rooms?' },
    { field: "fire_extinguisher_near_kitchen", label: 'Is there at least one fire extinguisher accessible near the kitchen?' },
    { type: "heading", label: 'Required Signage & Posted Information' },
    { field: "municipal_docs_posted", label: 'Is all necessary city/municipal documentation posted in a conspicuous area?' },
    { field: "grievance_policy_posted", label: 'Is the grievance policy and procedure posted in a conspicuous location that residents access frequently?' },
    { field: "mash_resident_bill_of_rights_posted", label: 'Are the MASH resident bill of rights posted in a conspicuous location that residents access frequently?' },
    { field: "emergency_policy_posted", label: 'Is the resident emergency policy and procedure posted in a conspicuous location?' },
    { field: "emergency_numbers_posted", label: 'Are emergency/non-emergency phone numbers posted in a conspicuous location?' },
    { field: "evacuation_map_posted", label: 'Is the evacuation map posted in a conspicuous location?' },
    { field: "staff_contact_posted", label: 'Is staff/management contact information posted in a conspicuous location?' },
    { type: "heading", label: 'Social & Community Environment' },
    { field: "smoke_free_living_environment", label: 'Does the property have a smoke free living environment?' },
    { field: "designated_smoking_area_with_disposal", label: 'Does the property have a designated smoking area with appropriate disposal containers for smoking materials, consistent with good neighbor practices?' },
    { field: "community_accomodation", label: 'Is there a community space large enough to reasonably accommodate community living and house meetings?' },
    { field: "group_area_living_room", label: 'Is there a comfortable group area or living room with couches where residents can informally socialize?' },
    { field: "kitchen_social_meals", label: 'Does the kitchen or dining area encourage residents to eat and share meals together?' },
    { field: "entertainment_recreational", label: 'Are there entertainment or recreational areas and/or furnishing that promote social engagement?' },
    { field: "decor_supports_recovery", label: 'Does the home have decor that supports and promotes recovery?' },
    { field: "activity_board_tracked", label: 'Is there a chore board or schedule posted and tracked that holds residents accountable to their assignments?' },
    { field: "community_board", label: 'Is there an activity or community board that promotes recovery-oriented community gatherings, social events, and other social activities among residents?' },
    { field: "community_resources_accessible", label: 'Can residents easily access and view a list of local community resources or directories?' },
    { field: "chores_accountability", label: 'Does it appear that residents are keeping up with their chores and programs requirements?' },
    { field: "safety_assessment_log", label: 'Is a log of completed self-safety assessments maintained onsite?' },
    { field: "safety_assessment_schedule", label: 'Does the self-safety assessment log comply with, at minimum, a monthly routine schedule?' },
    { type: "heading", label: 'Neighborhood & Curb Appeal' },
    { field: "typical_exterior", label: 'Exterior of the property does not have any indicators it is anything other than a single-family household typical of the neighborhood?' },
    { field: "home_meets_neighborhood_quality", label: 'Do the home and grounds meet or exceed the quality of the neighborhood?' },
    { field: "adequate_parking", label: 'Is there adequate parking for all residents and staff?' },
    { type: "heading", label: 'Resident Interview' },
    { field: "duration_of_residence", label: 'How long have you resided at this program?', custom_radio_labels: 'mash' },
    { field: "source_of_program_knowledge", label: 'How did you find this program or how were you referred here?', custom_radio_labels: 'mash' },
    { field: "out_of_state", label: 'Did you come from out of state?', custom_radio_labels: 'mash' },
    { field: "transportation_help", label: 'Did the provider help you with transportation to this facility/house? ', custom_radio_labels: 'mash' },
    { field: "exlanation_of_help", label: 'If yes, how did they help you specifically? ', custom_radio_labels: 'mash' },
    { field: "duration_of_treatment", label: 'Did you complete treatment or detox prior to coming here?', custom_radio_labels: 'mash' },
    { field: "insurance", label: 'Do you have insurance?', custom_radio_labels: 'mash' },
    { field: "orientation", label: 'When you moved in, did you go through an orientation with the provider?', custom_radio_labels: 'mash' },
    { field: "requirements_overview", label: 'Did the provider verbally go through all of their requirements?', custom_radio_labels: 'mash' },
    { field: "explanation_of_rules", label: 'Did you feel like all the rules and requirements were accurately explained to you?', custom_radio_labels: 'mash' },
    { field: "felt_welcome", label: 'Did you feel welcomed into the home? Were you introduced to your roommates at the time? ', custom_radio_labels: 'mash' },
    { field: "issue_with_residence", label: 'Have you had an issues since you\'ve moved in? If yes, what? Have you discussed it with management? What did they do?', custom_radio_labels: 'mash' },
    { field: "recovery_plan", label: 'Have you started to develop a recovery plan? Did management help you?', custom_radio_labels: 'mash' },
    { field: "enrollment", label: 'Are you enrolled in IOP or OP? If yes, did you pick the IOP or OP or were you referred to the IOP/OP? Was enrollment mandatory?', custom_radio_labels: 'mash' },
    { field: "doctor_visit", label: 'Have you seen a doctor since you\'ve been here? If yes, did the provider connect you to that doctor? Were you required to have a physical done for admission?', custom_radio_labels: 'mash' },
    { field: "feeling_like_home", label: 'Do you feel like this is your home? If no, why?', custom_radio_labels: 'mash' },
    { field: "frequency_of_drug_tests", label: 'How often are you drug tested? How much do you pay for each test? How often are you breathalyzed? Do you pay for those?', custom_radio_labels: 'mash' },
    { field: "professional_status", label: 'Are you working? Volunteering? Enrolled in school? Attending clinical programming? Where at? ', custom_radio_labels: 'mash' },
    { field: "grocery_shopping", label: 'Do you go grocery shopping for yourself?', custom_radio_labels: 'mash' },
    { field: "make_own_meals", label: 'Do you make your own meals or are meals provided for you? Do you make meals with your roommates?', custom_radio_labels: 'mash' },
    { field: "chores_assignment", label: 'Do you assist in maintenance and chores? How are chore assignments selected?', custom_radio_labels: 'mash' },
    { field: "household_expenses", label: 'Do you share any of the household expenses besides program fees?', custom_radio_labels: 'mash' },
    { field: "weekly_house_meetings", label: 'Do you attend weekly house meetings? What day and time?', custom_radio_labels: 'mash' },
    { field: "activity_participation", label: 'Have you participated in any recreational events or activities? What and when?', custom_radio_labels: 'mash' },
    { field: "resume_food_stamps", label: 'Has management assisted you in creating a resume, finding a job, applying for food stamps, linking you to educational and/or family services?', custom_radio_labels: 'mash' },
    { field: "rent_payments", label: 'How much are you paying for rent? How do you make your payments?', custom_radio_labels: 'mash' },
    { field: "grievance_policy", label: 'Are you aware of the grievance policy and procedure? Do you know what MASH does?', custom_radio_labels: 'mash' },
    { field: "program_change", label: 'If you could change anything about the program what would it be?', custom_radio_labels: 'mash' }
  ];
  
  const defaultFields = [
    { field: "event_relapse", label: 'What happens in the event of a relapse?' },
    { field: "event_overdose", label: 'What happens in the event of an overdose? Where do you keep your Narcan?' },
    { field: "house_meetings", label: 'Do you hold mandatory house meetings?' },
    { field: "topics_discussed", label: 'What day / time? What topics are discussed?' },
    { field: "assists_residents_recovery", label: 'Do you assist residents in finding recovery mentor or mutual aid sponsor if they are having difficulty finding one?' },
    { field: "host_social_events", label: 'Do you host and/or engage in sober social events? What events or activities have you participated in lately?' },
    { field: "resident_feedback", label: 'Do you allow residents to give feedback on their experience in order to enhance your program? How is this conducted?' },
    { field: "furnishings_typical", label: 'Are furnishings typical to those found in a single family home or apartment as opposed to institutional settings?' },
    { field: "entrances_and_exits", label: 'Are entrances and exits home-like as opposed to institutional or clinical?' },
    { field: "sleeping_room", label: 'Is there a minimum of 50 sq ft per client per sleeping room?' },
    { field: "resident_bathrooms", label: 'Is there 1 full bathroom for every 6 residents?' },
    { field: "storage_space", label: 'Does each resident have a responsible identified storage space?' },
    { field: "washer_available", label: 'Does the property have a washer/dryer available to all clients or are laundry services provided to residents?' },
    { field: "working_order", label: 'Are the appliances clean and in working order?' },
    { field: "repairs_maintenance", label: 'Does the program have a staffing plan that provides for addressing repairs and maintenance issues?' },
    { field: "smoke_detector", label: 'Does each sleeping room have a functioning smoke detector?' },
    { field: "emergency_escape", label: 'Does each sleeping room have an egress or secondary means of escape in event of emergency?' },
    { field: "gas_appliances", label: 'Are there functioning carbon monoxide detectors on each floor, if there are gas appliances?' },
    { field: "mounted_fire_extinguishers", label: 'Are there mounted fire extinguishers on each floor?' },
    { field: "fire_hazardous", label: 'Is the interior of the home functional, safe, of clean condition, and free of fire hazardous?' },
    { field: "exterior_fire_hazardous", label: 'Is the exterior of the home functional, safe, of clean condition, and free of fire hazards?' },
    { field: "smoke_free_living_environment", label: 'Does the property have a smoke free living environment?' },
    { field: "smoking_area_consistent", label: 'Does the property have a designated smoking area consistent with good neighbor practices?' },
    { field: "community_accomodation", label: 'Is there a community space large enough to reasonably accommodate community living and house meetings?' },
    { field: "group_area_living_room", label: 'Is there a comfortable group area or living room with couches where residents can informally socialize?' },
    { field: "kitchen_social_meals", label: 'Does the kitchen or dining area encourage residents to eat and share meals together?' },
    { field: "entertainment_recreational", label: 'Are there entertainment or recreational areas and/or furnishing that promote social engagement?' },
    { field: "furniture_condition", label: 'Is furniture in good condition and clean?' },
    { field: "typical_exterior", label: 'Exterior of the property does not have any indicators it is anything other than a single-family household typical of the neighborhood?' },
    { field: "adequate_parking", label: 'Is there adequate parking for all residents and staff?' },
    { field: "grievance_policy_posted", label: 'Is the grievance policy and procedure posted in a conspicuous location that residents access frequently?' },
    { field: "resident_rights_posted", label: 'Are the resident rights posted in a conspicuous location that residents access frequently?' },
    { field: "emergency_policy_posted", label: 'Is the resident emergency policy and procedure posted in a conspicuous location?' },
    { field: "emergency_numbers_posted", label: 'Are emergency/non-emergency phone numbers posted in a conspicuous location? Is the evacuation map posted in a conspicuous location?' },
    { field: "staff_contact_posted", label: 'Is staff/management contact information posted in a conspicuous location?' },
    { field: "decor_supports_recovery", label: 'Does the home have decor that supports and promotes recovery?' },
    { field: "activity_board_tracked", label: 'Is there a chore board or schedule posted and tracked that holds residents accountable to their assignments?' },
    { field: "community_board", label: 'Is there an activity or community board that promotes recovery-oriented community gatherings, social events, and other social activities among residents?' },
    { field: "chores_accountability", label: 'Does it appear that residents are keeping up with their chores and programs requirements?' },
    { field: "safety_assessment_log", label: 'Is a log of completed self-safety assessments maintained onsite?' },
    { field: "safety_assessment_schedule", label: 'Does the self-safety assessment log comply with, at minimum, a monthly routine schedule?' },
    { field: "duration_of_residence", label: 'How long have you resided at this program?' },
    { field: "source_of_program_knowledge", label: 'How did you find this program or how were you referred here?' },
    { field: "out_of_state", label: 'Did you come from out of state?' },
    { field: "transportation_help", label: 'Did the provider help you with transportation to this facility/house? ' },
    { field: "exlanation_of_help", label: 'If yes, how did they help you specifically? ' },
    { field: "duration_of_treatment", label: 'Did you complete treatment or detox prior to coming here?' },
    { field: "insurance", label: 'Do you have insurance?' },
    { field: "orientation", label: 'When you moved in, did you go through an orientation with the provider?' },
    { field: "requirements_overview", label: 'Did the provider verbally go through all of their requirements?' },
    { field: "explanation_of_rules", label: 'Did you feel like all the rules and requirements were accurately explained to you?' },
    { field: "felt_welcome", label: 'Did you feel welcomed into the home? Were you introduced to your roommates at the time? ' },
    { field: "issue_with_residence", label: 'Have you had an issues since you\'ve moved in? If yes, what? Have you discussed it with management? What did they do?' },
    { field: "recovery_plan", label: 'Have you started to develop a recovery plan? Did management help you?' },
    { field: "enrollment", label: 'Are you enrolled in IOP or OP? If yes, did you pick the IOP or OP or were you referred to the IOP/OP? Was enrollment mandatory?' },
    { field: "doctor_visit", label: 'Have you seen a doctor since you\'ve been here? If yes, did the provider connect you to that doctor? Were you required to have a physical done for admission?' },
    { field: "feeling_like_home", label: 'Do you feel like this is your home? If no, why?' },
    { field: "frequency_of_drug_tests", label: 'How often are you drug tested? How much do you pay for each test? How often are you breathalyzed? Do you pay for those?' },
    { field: "professional_status", label: 'Are you working? Volunteering? Enrolled in school? Attending clinical programming? Where at? ' },
    { field: "grocery_shopping", label: 'Do you go grocery shopping for yourself?' },
    { field: "make_own_meals", label: 'Do you make your own meals or are meals provided for you? Do you make meals with your roommates?' },
    { field: "chores_assignment", label: 'Do you assist in maintenance and chores? How are chore assignments selected?' },
    { field: "household_expenses", label: 'Do you share any of the household expenses besides program fees?' },
    { field: "weekly_house_meetings", label: 'Do you attend weekly house meetings? What day and time?' },
    { field: "activity_participation", label: 'Have you participated in any recreational events or activities? What and when?' },
    { field: "resume_food_stamps", label: 'Has management assisted you in creating a resume, finding a job, applying for food stamps, linking you to educational and/or family services?' },
    { field: "rent_payments", label: 'How much are you paying for rent? How do you make your payments?' },
    { field: "grievance_policy", label: 'Are you aware of the grievance policy and procedure? Do you know what FSARR does?' },
    { field: "program_change", label: 'If you could change anything about the program what would it be?' }
  ];

  const allFields = isMash ? mashFields : defaultFields

  const filteredFields = isScarr
    ? allFields.filter(
        (f) =>
          ![
            "house_meetings",
            "topics_discussed",
            "assists_residents_recovery",
            "resident_feedback",
            "emergency_escape",
            "smoke_free_living_environment",
            "safety_assessment_schedule",
            "duration_of_residence",
            "source_of_program_knowledge",
            "out_of_state",
            "transportation_help",
            "exlanation_of_help",
            "duration_of_treatment",
            "insurance",
            "orientation",
            "requirements_overview",
            "explanation_of_rules",
            "felt_welcome",
            "issue_with_residence",
            "recovery_plan",
            "enrollment",
            "doctor_visit",
            "feeling_like_home",
            "frequency_of_drug_tests",
            "professional_status",
            "grocery_shopping",
            "make_own_meals",
            "chores_assignment",
            "household_expenses",
            "weekly_house_meetings",
            "activity_participation",
            "resume_food_stamps",
            "rent_payments",
            "grievance_policy",
            "program_change",
          ].includes(f.field)
      )
    : allFields;

  return (
    <div id="inspection-view">
      <form>
        {isMash && (
          <div>
            <h5>General Information</h5>
          </div>
        )}
        <div className="mb-3">
          <label className="form-label fw-bold">
            <abbr title="required">* </abbr>
            Inspector Name:
          </label>
          <input
            type="text"
            className="form-control mb-1"
            value={inspection.inspector_name}
            disabled
            required
          />
        </div>
        <div className="mb-3">
          <label className="form-label fw-bold">
            <abbr title="required">* </abbr>
            Date of Inspection:
          </label>
          <input
            type="text"
            className="form-control mb-1"
            value={moment.utc(inspection.inspection_date).format('MM/DD/YYYY')}
            disabled
            required
          />
        </div>
        <div className="mb-3">
          <label className="form-label fw-bold">
            <abbr title="required">* </abbr>
            Reason:
          </label>
          <input
            type="text"
            className="form-control mb-1"
            value={inspection.inspection_reason}
            disabled
            required
          />
        </div>
        
        {isMash ? inspection.policy_sections.map((section, idx) => (
            <div key={idx}>
              <h5 className="mb-3">{section.heading}</h5>

              {section.questions.map((q, qIdx) => (
                <div key={q.field || qIdx} className="mb-3">
                  <label className="form-label fw-bold">{q.label}</label>

                  {q.notes && (
                    <textarea
                      className="form-control mb-1"
                      value={q.notes}
                      disabled
                    />
                  )}

                  <div className="mb-3 mt-3">
                    {formatAnswer(q.value, q.custom_radio_labels)}
                  </div>
                </div>
              ))}
            </div>
          )) : 
            Object.entries(policy_fields).map(([key, value]) => {
              if (key.endsWith("_label") || key.endsWith("_custom_radio_labels") || key.endsWith("_notes")) return null;
    
              const label = policy_fields[`${key}_label`] || key;
              const notes = policy_fields[`${key}_notes`];
              const heading = policy_fields[`${key}_type`] || null;
    
              return (
                <div key={key} className="mb-3">
                  {heading && (
                    <div>
                      <h5>{heading}</h5>
                    </div>
                  )}
                  <label className="form-label fw-bold">{label}</label>
    
                  {notes && (
                    <textarea
                      className="form-control mb-1"
                      value={notes}
                      disabled
                    />
                  )}
    
                  <div className="mb-3 mt-3">
                    {formatAnswer(value)} 
                  </div>
                </div>
              );
            })
          }


        {isMash && (
          <div>
            <h5>Certfication Status</h5>
          </div>
        )}
        <div className="mb-3">
          <label className="form-label fw-bold">What is the result of the inspection?</label>
          {inspection.inspection_result_notes && (
            <div className="mb-3">
              <input
                type="text"
                className="form-control mb-1"
                value={inspection.inspection_result_notes}
                disabled
              />
            </div>
          )}
          {isMash ? 
            <div className="mb-3">
              <div className="pl-3">
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="inspection_result"
                    id="inspection_result_passed"
                    checked={inspection.inspection_result === 'passed'}
                    disabled
                  />
                  <label className="form-check-label" htmlFor="inspection_result_passed">
                    Passed
                  </label>
                </div>
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="inspection_result"
                    id="inspection_result_failed"
                    checked={inspection.inspection_result?.toLowerCase() === 'failed'}
                    disabled
                  />
                  <label className="form-check-label" htmlFor="inspection_result_failed">
                    Failed
                  </label>
                </div>
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="inspection_result"
                    id="inspection_result_corrective_action"
                    checked={inspection.inspection_result === 'needs_corrective_action'}
                    disabled
                  />
                  <label className="form-check-label" htmlFor="inspection_result_corrective_action">
                    Corrective Action Plan
                  </label>
                </div>
              </div>
            </div>
            : <input
              type="text"
              className="form-control mb-1"
              value={inspection.inspection_result.replace(/[_-]/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2').replace(/\b\w/g, c => c.toUpperCase())}
              disabled
            />
          }
        </div>
        
        <div className="mb-3">
          <label className="form-label fw-bold">Feedback:</label>

          <textarea
            className="form-control mb-1"
            value={inspection.feedback}
            disabled
          />
        </div>
        
        <div className="mb-3">
          <label className="form-label fw-bold">
            <abbr title="required">* </abbr>
            Would you like to send a copy of this inspection to the facility?
          </label>
          <div className="pl-3">
            <div className="form-check">
              <input
                className="form-check-input"
                type="radio"
                name="send_copy"
                id="send_copy_yes"
                checked={inspection.send_copy === true}
                disabled
              />
              <label className="form-check-label" htmlFor="send_copy_yes">
                Yes
              </label>
            </div>
            <div className="form-check">
              <input
                className="form-check-input"
                type="radio"
                name="send_copy"
                id="send_copy_no"
                checked={inspection.send_copy === false}
                disabled
              />
              <label className="form-check-label" htmlFor="send_copy_no">
                No
              </label>
            </div>
          </div>
        </div>

        <div className="d-flex justify-content-end mt-4">
          <button className="btn btn-secondary" onClick={onClose}>Done</button>
        </div>
      </form>
    </div>
  );
};

export default InspectionForm;
